/**
 * What kind of failure this was, which is not the same question as what to say
 * about it.
 *
 * The distinction that matters is `UNREACHABLE` vs everything else: a server
 * that answered with a 404 broke one endpoint, a server that did not answer at
 * all broke the whole app. Only the second one justifies taking over the screen,
 * and from inside a component the two look identical — the request failed either
 * way — so somebody has to tell them apart here.
 *
 * `TIMEOUT` is neither: something is there, and this request took too long. It
 * stays with its screen, which keeps what it had.
 */
export const API_ERROR_KIND = {
  UNREACHABLE: 'unreachable',
  TIMEOUT: 'timeout',
  SERVER: 'server',
  NOT_FOUND: 'notFound',
  FORBIDDEN: 'forbidden',
  API: 'api',
};

/**
 * Gateways answering for the application: 502 and 503 when it is down, 504 when
 * it did not answer this request in time.
 */
const GATEWAY_STATUSES = [502, 503, 504];

/**
 * A failure with no response after this long was a slow answer, not a dead
 * server: those fail within seconds (refused connection, no network, 502/503),
 * while the load balancer's 504 comes after its idle timeout and without CORS
 * headers, so the browser hides its status.
 */
export const SLOW_ANSWER_MS = 30000;

const DEFAULT_MESSAGES = {
  [API_ERROR_KIND.UNREACHABLE]: 'No hay conexión con el servidor.',
  [API_ERROR_KIND.TIMEOUT]: 'El servidor tardó demasiado en responder.',
  [API_ERROR_KIND.SERVER]: 'El servidor tuvo un problema procesando la solicitud.',
  [API_ERROR_KIND.NOT_FOUND]: 'No encontramos lo que buscabas.',
  [API_ERROR_KIND.FORBIDDEN]: 'No tienes permiso para hacer esto.',
};

/**
 * Fixed sentences the backend writes in English, in the words the app uses.
 *
 * A bridge until the backend says them in Spanish: Django's own messages
 * cannot be switched globally, because `exposed_api` serves them to outside
 * clients that may be matching on them.
 */
const KNOWN_BACKEND_MESSAGES = {
  'Authentication failed': 'Credenciales inválidas.',
  'You do not have permission to access this resource': DEFAULT_MESSAGES[API_ERROR_KIND.FORBIDDEN],
  'You do not have permission to perform this action.': DEFAULT_MESSAGES[API_ERROR_KIND.FORBIDDEN],
  'No permissions to perform this action!': DEFAULT_MESSAGES[API_ERROR_KIND.FORBIDDEN],
  "You dont have permissions in this section!": DEFAULT_MESSAGES[API_ERROR_KIND.FORBIDDEN],
  'Not found.': DEFAULT_MESSAGES[API_ERROR_KIND.NOT_FOUND],
  'This field is required.': 'Este campo es obligatorio.',
  'This field may not be blank.': 'Este campo no puede quedar vacío.',
  'This field may not be null.': 'Este campo es obligatorio.',
  'A valid integer is required.': 'Debe ser un número entero.',
  'Enter a valid email address.': 'Ingresa un correo válido.',
  'Enter a valid URL.': 'Ingresa una URL válida.',
};

const translate = (message) => KNOWN_BACKEND_MESSAGES[message.trim()] ?? message;

/**
 * Whether a response body is a page the server rendered rather than a message
 * it wrote for us.
 *
 * Django answers with HTML for anything that never reached a DRF view — an
 * unmatched URL, an unhandled exception, a proxy error. Those bodies are
 * strings, so without this check they sail through as "the error message" and
 * the whole debug page ends up inside a red banner. It happens in production
 * too: `DEBUG=False` only makes the page shorter.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
const isServerPage = (value) => {
  if (typeof value !== 'string') return false;
  return /^\s*<(!doctype|html|\?xml)/i.test(value);
};

/**
 * Flattens whatever DRF nested under an error key into a list of strings.
 * @param {unknown} value
 * @returns {string[]}
 */
const flatten = (value) => {
  if (typeof value === 'string') return value.trim() ? [translate(value)] : [];
  if (Array.isArray(value)) return value.flatMap(flatten);
  if (value && typeof value === 'object') return Object.values(value).flatMap(flatten);
  return [];
};

/** Keys DRF uses for the request as a whole rather than for one field. */
const NON_FIELD_KEYS = ['non_field_errors', 'detail', 'error', 'message'];

/**
 * The per-field messages of a serializer error, `{campo: 'mensaje'}`, so a form
 * can put each one next to its input. Empty when the body is not field-shaped.
 *
 * @param {unknown} data - `error.response.data`
 * @returns {Object<string, string>}
 */
const readFieldErrors = (data) => {
  const container = data?.error && typeof data.error === 'object' && !Array.isArray(data.error)
    ? data.error
    : data;
  if (!container || typeof container !== 'object' || Array.isArray(container)) return {};

  return Object.entries(container).reduce((fields, [key, value]) => {
    if (NON_FIELD_KEYS.includes(key)) return fields;
    const messages = flatten(value);
    if (messages.length > 0) fields[key] = messages.join(' ');
    return fields;
  }, {});
};

/**
 * Reads the sentence the backend actually wrote, or nothing.
 *
 * The API answers with three shapes depending on the endpoint: `{message}`,
 * `{error: "texto"}` and `{error: {campo: ["msg"]}}` — DRF files whatever a
 * serializer's `validate()` raised under `non_field_errors`. Reading only one of
 * them is why creating a duplicate alliance used to show a generic fallback
 * instead of "Ya existe una alianza con este Admin".
 *
 * @param {unknown} data - `error.response.data`
 * @returns {string|null}
 */
const readBackendMessage = (data) => {
  if (isServerPage(data)) return null;
  if (typeof data === 'string') return data.trim() ? translate(data) : null;
  if (!data || typeof data !== 'object') return null;

  const container = data.error ?? data.message ?? data.detail ?? data;
  const messages = flatten(container?.non_field_errors ?? container);

  return messages.length > 0 ? messages.join(' ') : null;
};

/**
 * A 401 that a view wrote by hand, as opposed to DRF failing to authenticate.
 *
 * Many views answer a missing permission with 401 and `{error: "..."}`, where
 * DRF's own authentication failures carry `{detail: "..."}`. Only the second
 * one means the session is gone; reading the first as an expired session
 * signed people out for asking for something they may not do.
 */
const isRefusalWithin401 = (response) =>
  response?.status === 401 &&
  Boolean(response.data) &&
  typeof response.data === 'object' &&
  'error' in response.data;

/**
 * Sorts an axios rejection into a kind, and finds the best sentence for it.
 *
 * @param {unknown} error - The rejection from axios.
 * @returns {{kind: string, status: number|null, message: string|null}}
 *   `message` is what the backend wrote, or `null` when it wrote nothing usable
 *   — the caller decides what to say instead.
 */
export const classifyApiError = (error) => {
  const response = error?.response;

  /*
   * Not every rejection came from axios. A thunk that throws on its own —
   * "No hay etiquetas", or a plain programming mistake — has no `response`
   * either, and calling that a dead server was worse than useless: the label
   * download reported "No hay conexión con el servidor" while the server was
   * answering perfectly, because every reason it could fail arrived here as an
   * Error of its own. Its message is the truth; the kind is nobody's business.
   */
  if (!response && !error?.isAxiosError) {
    const message = typeof error?.message === 'string' ? error.message.trim() : '';
    return { kind: API_ERROR_KIND.API, status: null, message: message || null };
  }

  // No response at all: DNS, refused connection, a blocked preflight, the
  // machine being offline. Axios reports these with `response === undefined`.
  // `elapsedMs` comes from `services/requestTiming`.
  if (!response) {
    const kind =
      error?.code === 'ECONNABORTED' ||
      error?.code === 'ETIMEDOUT' ||
      error?.elapsedMs >= SLOW_ANSWER_MS
        ? API_ERROR_KIND.TIMEOUT
        : API_ERROR_KIND.UNREACHABLE;

    return { kind, status: null, message: null };
  }

  const { status } = response;
  const message = readBackendMessage(response.data);

  // A gateway status with a JSON sentence was written by the application — a
  // courier's API failing behind it, say — so the app is up and has something
  // to say. Only an empty or HTML gateway page means the gateway is talking.
  if (GATEWAY_STATUSES.includes(status)) {
    if (message && !isServerPage(response.data) && typeof response.data === 'object') {
      return { kind: API_ERROR_KIND.SERVER, status, message };
    }
    return {
      kind: status === 504 ? API_ERROR_KIND.TIMEOUT : API_ERROR_KIND.UNREACHABLE,
      status,
      message: null,
    };
  }
  if (status >= 500) return { kind: API_ERROR_KIND.SERVER, status, message };
  if (status === 404) return { kind: API_ERROR_KIND.NOT_FOUND, status, message };
  if (status === 403 || isRefusalWithin401(response)) {
    return { kind: API_ERROR_KIND.FORBIDDEN, status, message };
  }

  return { kind: API_ERROR_KIND.API, status, message };
};

/**
 * Whether this rejection means the session is over, so the app has to sign out.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isSessionExpired = (error) =>
  error?.response?.status === 401 && !isRefusalWithin401(error.response);

/**
 * Whether this request got no answer, as opposed to one endpoint answering badly
 * — or than something in the browser having thrown, which is not the server's
 * fault and must not black out the screen.
 *
 * A timeout counts: a change that timed out may still have been applied. What
 * it does not do is take the screen; see {@link isTimedOut}.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isApiUnreachable = (error) => {
  const { kind } = classifyApiError(error);
  return kind === API_ERROR_KIND.UNREACHABLE || kind === API_ERROR_KIND.TIMEOUT;
};

/**
 * Whether this request was too slow rather than unanswered: the server is there,
 * this one request took too long.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isTimedOut = (error) => classifyApiError(error).kind === API_ERROR_KIND.TIMEOUT;

/**
 * The message to show for an axios error.
 *
 * Order of preference: what the backend wrote, then something specific to the
 * kind of failure, then the caller's fallback. The caller's fallback goes last
 * on purpose — "No se pudieron obtener los pickups" is true but useless when the
 * real answer is that there is no server.
 *
 * @param {unknown} error - The rejection from axios.
 * @param {string} fallback - Used when nothing better is available.
 * @returns {string}
 */
export const extractApiError = (error, fallback) => {
  const { kind, message } = classifyApiError(error);

  return message || DEFAULT_MESSAGES[kind] || fallback;
};

/**
 * {@link extractApiError} plus what a form needs to point at the field.
 *
 * @param {unknown} error - The rejection from axios.
 * @param {string} fallback
 * @returns {{message: string, fieldErrors: Object<string, string>, status: number|null, kind: string}}
 */
export const extractApiErrorDetail = (error, fallback) => {
  const { kind, status } = classifyApiError(error);
  const data = error?.response?.data;

  return {
    message: extractApiError(error, fallback),
    fieldErrors: isServerPage(data) ? {} : readFieldErrors(data),
    status,
    kind,
  };
};

/**
 * The sentence a successful answer carries, if any. The API puts it under
 * `message`, `success` or `detail` depending on the endpoint.
 *
 * @param {unknown} data - `response.data`
 * @returns {string|null}
 */
export const readSuccessMessage = (data) => {
  if (!data || typeof data !== 'object') return null;
  const sentence = [data.message, data.success, data.detail].find(
    (value) => typeof value === 'string' && value.trim()
  );
  return sentence ? translate(sentence) : null;
};
